// The word cloud an answer-variants question can show instead of its chart, ported from
// block_base.coffee#renderWordCloudModel and the open-ended block the coffee view fed it to.
//
// There are no verbatims behind this cloud: the words ARE the answer variants, sized by how often
// each was picked. So the corpus is synthesized here rather than fetched, handed to an open-ended
// block locked to its cloud view, and that block draws it like any other.

import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import { isRType as calcIsRType, removeLogicFromText } from '@aytm/app/stats/page/components/lib/utils'
import { removeTagsFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { OE_CHART_VIEW_MODES, OE_HEALS_VIEW_MODE } from '@aytm/app/stats/page/question-views/oe/lib/family'

// what the cloud block is locked to: its one view mode, drawn from the corpus given rather than
// from verbatims it would otherwise fetch
const WC_FORMAT = { viewModes: ['wordcloud'], viewMode: 'wordcloud', useRawData: true }

// the surfaces an ordinary open-ended block offers that this one has nothing to fill
const WC_COMPONENTS = { oe_answers: false, table: false }

// Max-diff is the one type whose items were never picked a countable number of times: its answer
// carries probabilities, already a share of a hundred, so it weighs and scales by those instead.
const MD_TYPE = 'md'
const MD_TOTAL = 100

const cloudWord = (text, frequency) => [removeTagsFromText(removeLogicFromText(text)), frequency, 0, false]

// An item weighs as much as it was picked -- by respondents or by cases, whichever the report counts.
const frequencyOf = (value = {}, { type, byCases }) => {
  if (MD_TYPE === type) return value.varyingProbability || 0

  return (byCases ? value.amount : value.respAmount) || 0
}

// The corpus: every batch, or every answer variant when the question has no batches.
const calcCloudWords = ({ answer = {}, type, batches, answerVariants, byCases }) => {
  const weigh = { type, byCases }

  if (batches?.length) {
    return batches.filter(Boolean).map(b => cloudWord(b.text, frequencyOf(answer[`b${b.position}`], weigh)))
  }

  if (answerVariants?.length) {
    return answerVariants.filter(Boolean).map(av => cloudWord(av.text, frequencyOf(answer[`a${av.position}`], weigh)))
  }

  return []
}

// The answer an ordinary open-ended block would have been handed, standing in for verbatims that
// were never collected.
export const calcCloudAnswer = ({ answer = {}, type, batches, answerVariants, rType } = {}) => {
  const byCases = null == rType || 'cases' === rType
  const total = MD_TYPE === type ? MD_TOTAL : (byCases && answer.total) || answer.respTotal || 0

  return {
    cloudWords: {
      junkArray: [],
      noun: {},
      sentences: [],
      spell: {},
      words: calcCloudWords({ answer, type, batches, answerVariants, byCases })
    },
    comments: total,
    total
  }
}

export const calcWordCloudProps = ({
  id,
  questionKey,
  type,
  variant,
  questionType,
  // a checkbox matrix sub-item names its kind only by type and sub-type, so both decide whether it counts responses
  questionSubType,
  format,
  components,
  answer,
  answerVariants,
  batches,
  modelDefaults,
  clickable,
  batch,
  link,
  isReportXpert,
  params = {},
  handlers = {},
  onWcParamsChange
} = {}) =>
  ({
    id,
    questionKey,
    type,
    format: { ...format, ...WC_FORMAT },
    components: { ...components, ...WC_COMPONENTS },
    answer: calcCloudAnswer({
      answer,
      type,
      batches,
      answerVariants,
      rType: calcIsRType({ type, variant, questionType, questionSubType }) ? getModelValue('rType') : 'cases'
    }),
    clickable,
    questionChartMode: getModelValue('chartMode'),
    chartViewModes: OE_CHART_VIEW_MODES,
    healViewMode: OE_HEALS_VIEW_MODE,
    modelDefaults,
    // only the cloud's own section travels: the chart params this question owns say which of the two
    // is on screen, and a block drawing the cloud must not answer for them
    params: { wc: params.wc },
    labelTarget: { id, questionId: id, batch },
    commentsTarget: { id, questionId: id, link, batch, isReportXpert },
    handlers,
    onWcParamsChange,
    onReady: handlers.onWcReady
  })
